// ═══════════════════════════════════════════════════════════════════════════════
//  src/advanced/advancedApi.js
//  ALTO RENDIMIENTO — la única capa que habla con el servidor
//
//  Cuatro de los seis formatos no aparecen en este archivo, y eso es el diseño,
//  no un olvido: Tone Surgery, Subtexto, Precisión léxica y Oído nativo tienen
//  clave de respuesta, así que se resuelven enteros en el navegador. Cero
//  tokens, cero latencia, funcionan sin conexión.
//
//  Sólo el debate y el correo llegan aquí, porque ahí no hay respuesta correcta
//  contra la cual comparar.
// ═══════════════════════════════════════════════════════════════════════════════

const BASE = `${import.meta.env.VITE_API_URL || ''}/api/v3/advanced`;

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function request(path, options = {}) {
  let res;
  try {
    res = await fetch(`${BASE}${path}`, { headers: authHeaders(), ...options });
  } catch {
    // Sin red, `fetch` lanza y el navegador pone su propio texto —«Failed to
    // fetch»— que llega tal cual a la pantalla del alumno. En un mercado global
    // con red irregular esa frase se va a ver, así que se dice en su idioma y se
    // dice qué hacer.
    const err = new Error('No hay conexión con el servidor. Revisa tu red e inténtalo otra vez.');
    err.status = 0;
    throw err;
  }

  if (!res.ok) {
    let detail = 'No pudimos conectar con la sala de alto rendimiento.';
    try {
      const body = await res.json();
      if (typeof body?.detail === 'string') detail = body.detail;
      else if (body?.detail?.message) detail = body.detail.message;
    } catch { /* respuesta sin JSON */ }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }

  return res.json();
}

const post = (path, body) => request(path, { method: 'POST', body: JSON.stringify(body) });

/** Debates y encargos de correo. Sin costo. */
export const getCatalog = () => request('/catalog');

/** El contraataque de media sesión, contra el punto más débil de lo dicho. */
export const getCounter = ({ debateId, side, transcript }) =>
  post('/debate/counter', { debate_id: debateId, side, transcript });

/** Evaluación del argumento sostenido. */
export const evaluateDebate = ({ debateId, side, transcript, seconds }) =>
  post('/debate/evaluate', {
    debate_id: debateId, side, transcript,
    duration_seconds: Math.max(0, Math.min(600, seconds || 0)),
  });

/** Evaluación del correo: longitud por código, registro por IA. */
export const evaluateEmail = ({ emailId, draft }) =>
  post('/email/evaluate', { email_id: emailId, draft });

/* ── LA SITUACIÓN ──────────────────────────────────────────────────────────────
   El séptimo formato. Tres puertas en vez de dos, porque aquí la conversación
   es de ida y vuelta: cada turno del otro es una llamada, y el veredicto es
   otra.

   Las perillas (sector, situación, exigencia) NO se generan: son un catálogo
   fijo de 180 combinaciones que el servidor compone de piezas. Por eso
   `getSituacionOptions` no gasta un centavo y se puede pedir al entrar. */

/** Las perillas que la pantalla pinta, EN SU IDIOMA. Sin IA y sin costo.
 *
 *  Se manda lo que el alumno tenga elegido —"es", "en" o "auto"— y vuelve
 *  resuelto en `idioma`. Quien decide es el servidor: es el único que sabe su
 *  nivel, y con dos reglas (una aquí y otra allí) acabaría leyendo media
 *  pantalla en cada idioma el día que discreparan. */
export const getSituacionOptions = (idioma = 'auto') =>
  request(`/situacion/opciones?idioma=${encodeURIComponent(idioma)}`);

/** Lo que contesta el otro. El primer turno lo abre el escenario, sin IA. */
export const situacionTurn = ({ sector, situacion, exigencia, turnos }) =>
  post('/situacion/turno', { sector, situacion, exigencia, turnos });

/** El dictamen: ¿consiguió lo que iba a conseguir? No califica la gramática. */
export const situacionVerdict = ({ sector, situacion, exigencia, turnos,
                                   seconds, idioma = 'auto' }) =>
  post('/situacion/veredicto', {
    sector, situacion, exigencia, turnos, idioma,
    duration_seconds: Math.max(0, Math.min(1800, seconds || 0)),
  });

/** En qué idioma se le habla a este alumno: "es" o "en", nunca otra cosa.
 *
 *  Una sola fuente para toda la sala. Si cada formato lo dedujera por su
 *  cuenta, el día que dos reglas no coincidan el alumno leería media pantalla
 *  en cada idioma — y eso parece descuido, no decisión.
 *
 *  No gasta IA. Si falla, quien llama se queda en español y la sala funciona
 *  igual: el idioma nunca puede ser motivo de que algo no se abra. */
export const getIdioma = (elegido = 'auto') =>
  request(`/idioma?elegido=${encodeURIComponent(elegido)}`);

/** Deja constancia de una prueba terminada EN EL NAVEGADOR.
 *
 *  Las cuatro pruebas de opción múltiple se resuelven enteras en el equipo del
 *  alumno, así que este número llega DICHO por el cliente y el servidor lo
 *  marca como tal. Cuenta como constancia de entrenamiento, nunca como prueba
 *  de nivel — esa distinción es la que permite que el coach diga «estás
 *  mejorando» sin inventárselo. */
export const guardaResultado = ({ formato, aciertos, intentos, segundos }) =>
  post('/resultado', { formato, aciertos, intentos, segundos });
